import React, { useState } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import RichTextEditor from '../WidgetEditComponents/RichTextEditor';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function ComparisonWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    console.log('🔍 ComparisonWidgetEdit rendered:', {
        widgetId: widget.id,
        widgetType: widget.type,
        currentComparisonLayout: editData.comparisonLayout,
        hasHandleInputChange: !!handleInputChange
    });
    
    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log('🔍 Comparison layout change:', { layoutId, layoutConfig });
        
        // Wende alle Layout-Config-Werte auf einmal an
        if (layoutConfig) {
            console.log('🔍 Applying layout config:', layoutConfig);
            
            // Alle Werte auf einmal in editData setzen und direkt updaten
            const updatedEditData = { ...editData, ...layoutConfig };
            onUpdate(updatedEditData);
        }
    };

    // Upload States
    const [isUploadingBefore, setIsUploadingBefore] = useState(false);
    const [isUploadingAfter, setIsUploadingAfter] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Alle Text-Felder mit Show/Hide Toggles
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Main Title (H2)',
            type: 'input',
            placeholder: 'Your Transformation Journey',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        {
            fieldName: 'richTextContent',
            label: 'Transformation Description',
            type: 'textarea',
            placeholder: 'Describe your transformation journey here...',
            rows: 4,
            fontField: 'richTextFont',
            fontType: 'small',
            effectField: 'richTextEffect'
        },
        {
            fieldName: 'beforeLabel',
            label: 'Before Label',
            type: 'input',
            placeholder: 'Before',
            fontField: 'labelFont',
            fontType: 'small'
        },
        {
            fieldName: 'afterLabel',
            label: 'After Label',
            type: 'input',
            placeholder: 'After',
            fontField: 'labelFont',
            fontType: 'small'
        },
        {
            fieldName: 'beforeDate',
            label: 'Before Date',
            type: 'input',
            placeholder: 'January 2023',
            fontField: 'dateFont',
            fontType: 'small'
        },
        {
            fieldName: 'afterDate',
            label: 'After Date',
            type: 'input',
            placeholder: 'September 2024',
            fontField: 'dateFont',
            fontType: 'small'
        }
    ];

    // Color Configuration für MoreSettingsSection
    const colorConfig = [
        [
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#000000',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'richTextColor',
                label: 'Rich Text',
                defaultValue: '#333333',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'imageLabelColor',
                label: 'Image Labels',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'imageLabelBackgroundColor',
                label: 'Label Background',
                defaultValue: 'rgba(255, 255, 255, 0.9)',
                icon: '/img/background.svg'
            }
        ]
    ];

    // Font Settings Configuration für MoreSettingsSection
    const fontConfig = [
        {
            label: 'Title',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '42',
            defaultWeight: '700'
        },
        {
            label: 'Rich Text',
            sizeField: 'richTextSize',
            weightField: 'richTextWeight',
            defaultSize: '16',
            defaultWeight: '400'
        },
        {
            label: 'Image Labels',
            sizeField: 'imageLabelSize',
            weightField: 'imageLabelWeight',
            defaultSize: '14',
            defaultWeight: '600'
        }
    ];

    // 🚀 REVOLUTIONARY LAYOUT SETTINGS - Custom Properties für Comparison Widget
    const customLayoutProperties = [
        {
            section: 'Image Settings',
            icon: 'fa-image',
            properties: [
                {
                    id: 'imageWidth',
                    label: 'Image Width',
                    min: 200,
                    max: 600,
                    step: 10,
                    unit: 'px',
                    icon: '📷',
                    description: 'Width of before/after images'
                },
                {
                    id: 'imageHeight',
                    label: 'Image Height',
                    min: 150,
                    max: 500,
                    step: 10,
                    unit: 'px',
                    icon: '📷',
                    description: 'Height of before/after images'
                },
                {
                    id: 'imageOverlap',
                    label: 'Image Overlap',
                    min: 0,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    icon: '🔗',
                    description: 'Overlap between before/after images'
                },
                {
                    id: 'imageBorderRadius',
                    label: 'Image Border Radius',
                    min: 0,
                    max: 50,
                    step: 2,
                    unit: 'px',
                    icon: '🔲',
                    description: 'Border radius of images'
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - ALLE Comparison/Transformation Settings zusammengefasst
    const advancedConfig = [
        // Content Spacing Group
        {
            fieldName: 'contentSpacing',
            label: 'Content Side Spacing',
            type: 'slider',
            defaultValue: 40,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            color: '#10b981',
            icon: '/img/element.svg',
            description: 'Side spacing around transformation content (prevents touching edges)'
        },
        {
            fieldName: 'contentSpacingMobile',
            label: 'Content Side Spacing Mobile',
            type: 'slider',
            defaultValue: 20,
            min: 0,
            max: 60,
            step: 5,
            unit: 'px',
            color: '#10b981',
            icon: '/img/element.svg',
            description: 'Side spacing around transformation content on mobile'
        },
        
        // Dimensions Group
        {
            fieldName: 'height',
            label: 'Widget Height',
            type: 'number',
            defaultValue: 500,
            min: 400,
            max: 1000,
            step: 20,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'maxWidth',
            label: 'Widget Max Width',
            type: 'number',
            defaultValue: 1200,
            min: 800,
            max: 1600,
            step: 50,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'mobileImageMaxWidth',
            label: 'Mobile Image Max Width',
            type: 'slider',
            defaultValue: 280,
            min: 200,
            max: 400,
            step: 20,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#FF5722',
            description: 'Maximum width of images on mobile'
        },
        {
            fieldName: 'mobileImageHeight',
            label: 'Mobile Image Height',
            type: 'slider',
            defaultValue: 320,
            min: 200,
            max: 500,
            step: 20,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#9C27B0',
            description: 'Height of images on mobile'
        },

        // Spacing Group
        {
            fieldName: 'marginTop',
            label: 'Space Top',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'marginBottom',
            label: 'Space Bottom',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg',
            iconStyle: { rotate: '180deg' }
        },
        {
            fieldName: 'padding',
            label: 'Widget Padding',
            type: 'number',
            defaultValue: 60,
            min: 20,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'contentSpacing',
            label: 'Content Spacing',
            type: 'number',
            defaultValue: 60,
            min: 20,
            max: 100,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'imageOverlap',
            label: 'Desktop Image Overlap',
            type: 'slider',
            defaultValue: 25,
            min: 0,
            max: 350,
            step: 25,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#4CAF50',
            description: 'Overlap between before/after images on desktop'
        },
        {
            fieldName: 'mobileImageOverlap',
            label: 'Mobile Image Overlap',
            type: 'slider',
            defaultValue: 25,
            min: 0,
            max: 350,
            step: 25,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#FF9800',
            description: 'Overlap between before/after images on mobile'
        },

        // Display Group
        {
            fieldName: 'borderRadius',
            label: 'Widget Border Radius',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'imageLabelPadding',
            label: 'Label Padding',
            type: 'slider',
            defaultValue: 8,
            min: 4,
            max: 20,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#607D8B',
            description: 'Padding inside image labels'
        },
        {
            fieldName: 'imageLabelBorderRadius',
            label: 'Label Border Radius',
            type: 'slider',
            defaultValue: 6,
            min: 0,
            max: 20,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#795548',
            description: 'Border radius of image labels'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">

                {/* Comparison Layout Selector */}
                <div className="widget-trenner">
                    <h3>Comparison Layout</h3>
                    <hr className="hrrrr" />
                </div>
                
                <LayoutSelector
                    widgetType="comparisonWidget"
                    currentLayout={editData.comparisonLayout || 'text-left-images-right'}
                    selectedLayout={editData.comparisonLayout || 'text-left-images-right'}
                    onLayoutChange={handleLayoutChange}
                />

                {/* 🚀 REVOLUTIONARY TEXT SETTINGS - Alle Text-Felder mit Show/Hide Toggles */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                />

                {/* 🚀 BEFORE IMAGE CONTAINER - mit Width, Height, Border Radius & Shadow */}
                <ImageContainer
                    label="Before Image"
                    fieldName="beforeImage"
                    currentImage={editData.beforeImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBefore}
                    setIsUploading={setIsUploadingBefore}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Width',
                            fieldName: 'beforeImageWidth',
                            defaultValue: 240,
                            min: 180,
                            max: 400,
                            step: 10,
                            unit: 'px',
                            description: 'Width of the before image'
                        },
                        {
                            type: 'size',
                            label: 'Image Height',
                            fieldName: 'beforeImageHeight',
                            defaultValue: 300,
                            min: 200,
                            max: 450,
                            step: 10,
                            unit: 'px',
                            description: 'Height of the before image'
                        },
                        {
                            type: 'size',
                            label: 'Border Radius',
                            fieldName: 'beforeImageBorderRadius',
                            defaultValue: 12,
                            min: 0,
                            max: 30,
                            step: 2,
                            unit: 'px',
                            description: 'Border radius of before image'
                        },
                        {
                            type: 'toggle',
                            label: 'Image Shadow',
                            fieldName: 'beforeImageShadow'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* 🚀 AFTER IMAGE CONTAINER - mit Width, Height, Border Radius & Shadow */}
                <ImageContainer
                    label="After Image"
                    fieldName="afterImage"
                    currentImage={editData.afterImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingAfter}
                    setIsUploading={setIsUploadingAfter}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Width',
                            fieldName: 'afterImageWidth',
                            defaultValue: 240,
                            min: 180,
                            max: 400,
                            step: 10,
                            unit: 'px',
                            description: 'Width of the after image'
                        },
                        {
                            type: 'size',
                            label: 'Image Height',
                            fieldName: 'afterImageHeight',
                            defaultValue: 300,
                            min: 200,
                            max: 450,
                            step: 10,
                            unit: 'px',
                            description: 'Height of the after image'
                        },
                        {
                            type: 'size',
                            label: 'Border Radius',
                            fieldName: 'afterImageBorderRadius',
                            defaultValue: 12,
                            min: 0,
                            max: 30,
                            step: 2,
                            unit: 'px',
                            description: 'Border radius of after image'
                        },
                        {
                            type: 'toggle',
                            label: 'Image Shadow',
                            fieldName: 'afterImageShadow'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* Text Settings */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                />

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard + Custom Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* 🔧 ADVANCED SETTINGS - New Component replacing all scattered settings */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* More Settings (Colors & Fonts only) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Advanced Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />

            </div>
        </div>
    );
}

export default ComparisonWidgetEdit;
